import classnames from 'classnames';
import React from 'react';

import Icon from 'dpl/common/components/Icon';

export const ACTION_TYPES = {
  BUTTON: 'button',
  LINK: 'link'
} as const;

type ActionType = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES];

interface ActionButtonProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'type' | 'className'> {
  iconName: string;
  iconSize?: string;
  children?: React.ReactNode;
  /** Widened to allow null, which is what the callers' defaults pass. */
  className?: string | null;
  iconClassName?: string | null;
  /**
   * `LINK` renders an anchor instead of a button. No caller uses it today, so
   * the pass-through props are typed as a button's.
   */
  type?: ActionType;
}

export default function ActionButton({
  children = null,
  iconName,
  iconSize = '24px',
  iconClassName = null,
  className = null,
  type = ACTION_TYPES.BUTTON,
  ...buttonProps
}: ActionButtonProps) {
  // A union of two intrinsic tags can't be typed to accept both elements'
  // props, so the tag itself is erased. The props are still checked where they
  // enter, at this component's own boundary.
  const Component: any = type === ACTION_TYPES.LINK ? 'a' : 'button';

  return (
    <Component
      type={type === ACTION_TYPES.BUTTON ? 'button' : undefined}
      className={classnames('flex items-center gap-8', className)}
      {...buttonProps}
    >
      <Icon
        className={iconClassName}
        name={iconName}
        height={iconSize}
        width={iconSize}
      />
      {children && <span>{children}</span>}
    </Component>
  );
}
